import React, { useMemo, useState } from "react";
import {
  Button,
  Card,
  Table,
  Tabs,
  Typography,
  Space,
  Spin,
  Switch,
  Row,
  Col,
  Statistic,
  Divider,
} from "antd";
import {
  BarChartOutlined,
  InfoCircleOutlined,
  TeamOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  TrophyOutlined,
  LineChartOutlined,
  AreaChartOutlined,
  TableOutlined,
  DashboardOutlined,
  PartitionOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
} from "recharts";
import ContributorMetrics from "./contributor-metrics";
import ContributorSummary from "./contributor-summary";
import { CohortViews } from "./cohort";
import TimeframeSelector from "./TimeframeSelector";
import {
  formatCurrency,
  formatDate,
  formatNumber,
} from "../../utils/formatters";
import { colors } from "../../config/constants";

const { Title, Text } = Typography;

const PerformanceView = ({
  projectId,
  objectiveId,
  loading,
  metadata,
  overview,
  tableData,
  isWeekly,
  activeTab,
  chartData,
  lastSyncedAt,
  onRefreshPerformance,
  onRefreshContributorMetrics,
  contributorMetricsRefreshTrigger,
  onActiveTabChange,
  onToggleWeekly,
  timeframeDateRange = {},
}) => {
  const [performanceSubTab, setPerformanceSubTab] = useState("performance");

  const columns = useMemo(
    () => [
      {
        title: (
          <Space>
            <CalendarOutlined style={{ color: "#1890ff" }} />
            {isWeekly ? "Week Ending Date" : "Date"}
          </Space>
        ),
        dataIndex: "date",
        key: "weekEndDate",
        width: 180,
        sorter: (a, b) => {
          const dateA = new Date(a.date || 0);
          const dateB = new Date(b.date || 0);
          return dateA - dateB;
        },
        render: (text) => formatDate(text),
      },
      {
        title: (
          <Space>
            <TeamOutlined style={{ color: "#52c41a" }} />
            Contributors
          </Space>
        ),
        dataIndex: "contributors",
        key: "contributors",
        width: 160,
        align: "right",
        sorter: (a, b) => a.contributors - b.contributors,
        render: (text) => text || 0,
      },
      {
        title: (
          <Space>
            <ClockCircleOutlined style={{ color: "#fa8c16" }} />
            Total Hours
          </Space>
        ),
        dataIndex: "totalHours",
        key: "totalHours",
        width: 160,
        align: "right",
        sorter: (a, b) => a.totalHours - b.totalHours,
        render: (text) => (
          <Space>
            <Text style={{ fontSize: 15 }}>{formatNumber(text || 0)}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              hrs
            </Text>
          </Space>
        ),
      },
      {
        title: (
          <Space>
            <BarChartOutlined style={{ color: "#722ed1" }} />
            Total Units
          </Space>
        ),
        dataIndex: "totalUnits",
        key: "totalUnits",
        width: 140,
        align: "right",
        sorter: (a, b) => (a.totalUnits ?? 0) - (b.totalUnits ?? 0),
        render: (text) => formatNumber(text ?? 0, 0),
      },
      {
        title: (
          <Space>
            <LineChartOutlined style={{ color: "#13c2c2" }} />
            Average RPH
          </Space>
        ),
        dataIndex: "averageRph",
        key: "averageRph",
        width: 130,
        align: "right",
        sorter: (a, b) => (a.averageRph ?? 0) - (b.averageRph ?? 0),
        render: (text) => (text != null ? formatNumber(text) : "—"),
      },
      {
        title: (
          <Space>
            <TrophyOutlined style={{ color: "#eb2f96" }} />
            Average Quality
          </Space>
        ),
        dataIndex: "averageQuality",
        key: "averageQuality",
        width: 140,
        align: "right",
        sorter: (a, b) => (a.averageQuality ?? 0) - (b.averageQuality ?? 0),
        render: (text) => (text != null ? `${formatNumber(text)} %` : "—"),
      },
    ],
    [isWeekly],
  );

  if (!projectId && !objectiveId) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
        }}
      >
        <Card
          style={{
            borderRadius: 16,
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
            textAlign: "center",
            padding: "40px",
            maxWidth: 500,
          }}
        >
          <InfoCircleOutlined
            style={{ fontSize: 64, color: "#faad14", marginBottom: 16 }}
          />
          <Title level={3} style={{ marginTop: 16 }}>
            Missing Information
          </Title>
          <Text type="secondary" style={{ fontSize: 16 }}>
            No project or objective ID provided
          </Text>
        </Card>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        width: "100%",
        margin: "16px 0",
      }}
    >
      <div style={{ width: "100%" }}>
        <Card
          style={{
            borderRadius: 0,
            boxShadow:
              "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            border: "none",
          }}
          styles={{
            body: { padding: "0 24px 24px 24px", boxShadow: "none" },
          }}
        >
          {loading && !metadata ? (
            <div style={{ textAlign: "center", padding: 80 }}>
              <Spin size="large" description="Loading performance data..." />
            </div>
          ) : (
            <Tabs
              activeKey={performanceSubTab}
              onChange={setPerformanceSubTab}
              size="medium"
              type="card"
              style={{
                marginBottom: 0,
              }}
              tabBarExtraContent={
                <Space>
                  {onRefreshPerformance && (
                    <Button
                      icon={<ReloadOutlined />}
                      onClick={onRefreshPerformance}
                      loading={loading}
                    >
                      Refresh
                    </Button>
                  )}
                  {onRefreshPerformance && (
                    <Text type="secondary">
                      {lastSyncedAt
                        ? `Last refreshed at ${lastSyncedAt.toLocaleString()}`
                        : "No data in cache. Click Refresh to load."}
                    </Text>
                  )}
                </Space>
              }
              styles={{
                header: {
                  position: "sticky",
                  top: 118,
                  zIndex: 24,
                  paddingTop: 16,
                  backgroundColor: colors.white,
                },
                body: { padding: "0 24px 24px 24px" },
              }}
              items={[
                {
                  key: "performance",
                  label: (
                    <Space>
                      <DashboardOutlined />
                      <span>Performance</span>
                    </Space>
                  ),
                  children: (
                    <>
                      {/* Overview Section */}
                      {overview && (
                        <Card
                          title={
                            <Space>
                              <TrophyOutlined
                                style={{ color: "#faad14", fontSize: 18 }}
                              />
                              <span style={{ fontSize: 18, fontWeight: 600 }}>
                                Overview
                              </span>
                            </Space>
                          }
                          style={{
                            marginBottom: 24,
                            borderRadius: 12,
                            border: "1px solid #e8e8e8",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                          }}
                          variant="borderless"
                          styles={{
                            header: {
                              background:
                                "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
                              borderRadius: "12px 12px 0 0",
                              padding: "16px 24px",
                            },
                          }}
                        >
                          <Row gutter={[24, 24]}>
                            <Col xs={24} sm={12} md={8}>
                              <Card
                                variant="borderless"
                                style={{
                                  height: 164,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: "100%",
                                  borderRadius: 12,
                                  textAlign: "center",
                                  boxShadow: "0 0 15px rgba(0,0,0,0.15)",
                                }}
                                styles={{
                                  body: { padding: "20px" },
                                }}
                              >
                                {loading && <Spin size="small" />}
                                {!loading && (
                                  <>
                                    <TeamOutlined
                                      style={{
                                        fontSize: 32,
                                        color: "#0ea5e9",
                                        marginBottom: 12,
                                      }}
                                    />
                                    <Statistic
                                      title={
                                        <span
                                          style={{
                                            color: "#64748b",
                                            fontSize: 13,
                                          }}
                                        >
                                          Unique Contributors
                                        </span>
                                      }
                                      value={overview.uniqueContributors}
                                      suffix={
                                        <span
                                          style={{
                                            fontSize: 14,
                                            color: "#64748b",
                                          }}
                                        >
                                          people
                                        </span>
                                      }
                                      styles={{
                                        content: {
                                          color: "#0ea5e9",
                                          fontSize: 28,
                                          fontWeight: 700,
                                        },
                                      }}
                                    />
                                  </>
                                )}
                              </Card>
                            </Col>
                            <Col xs={24} sm={12} md={8}>
                              <Card
                                variant="borderless"
                                style={{
                                  height: 164,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  borderRadius: 12,
                                  textAlign: "center",
                                  boxShadow: "0 0 15px rgba(0,0,0,0.15)",
                                }}
                                styles={{
                                  body: { padding: "20px" },
                                }}
                              >
                                {loading && <Spin size="small" />}
                                {!loading && (
                                  <>
                                    <ClockCircleOutlined
                                      style={{
                                        fontSize: 32,
                                        color: "#f59e0b",
                                        marginBottom: 12,
                                      }}
                                    />
                                    <Statistic
                                      title={
                                        <span
                                          style={{
                                            color: "#64748b",
                                            fontSize: 13,
                                          }}
                                        >
                                          Total Hours
                                        </span>
                                      }
                                      value={formatNumber(overview.totalHours)}
                                      suffix={
                                        <span
                                          style={{
                                            fontSize: 14,
                                            color: "#64748b",
                                          }}
                                        >
                                          hrs
                                        </span>
                                      }
                                      styles={{
                                        content: {
                                          color: "#f59e0b",
                                          fontSize: 28,
                                          fontWeight: 700,
                                        },
                                      }}
                                    />
                                  </>
                                )}
                              </Card>
                            </Col>
                            <Col xs={24} sm={12} md={8}>
                              <Card
                                variant="borderless"
                                style={{
                                  height: 164,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  borderRadius: 12,
                                  textAlign: "center",
                                  boxShadow: "0 0 15px rgba(0,0,0,0.15)",
                                }}
                                styles={{
                                  body: { padding: "20px" },
                                }}
                              >
                                {loading && <Spin size="small" />}
                                {!loading && (
                                  <>
                                    <BarChartOutlined
                                      style={{
                                        fontSize: 32,
                                        color: "#ec4899",
                                        marginBottom: 12,
                                      }}
                                    />
                                    <Statistic
                                      title={
                                        <span
                                          style={{
                                            color: "#64748b",
                                            fontSize: 13,
                                          }}
                                        >
                                          Avg{" "}
                                          {isWeekly ? `hrs/Week` : `hrs/Day`}
                                        </span>
                                      }
                                      value={formatNumber(
                                        isWeekly
                                          ? overview.averageHoursPerWeek
                                          : overview.averageHoursPerDay,
                                      )}
                                      suffix={
                                        <span
                                          style={{
                                            fontSize: 14,
                                            color: "#64748b",
                                          }}
                                        >
                                          hrs
                                        </span>
                                      }
                                      styles={{
                                        content: {
                                          color: "#ec4899",
                                          fontSize: 28,
                                          fontWeight: 700,
                                        },
                                      }}
                                    />
                                  </>
                                )}
                              </Card>
                            </Col>
                          </Row>
                        </Card>
                      )}

                      {/* Performance Data Charts and Table */}
                      <Card
                        title={
                          <Space>
                            <DashboardOutlined
                              style={{ color: "#1890ff", fontSize: 18 }}
                            />
                            <span style={{ fontSize: 18, fontWeight: 600 }}>
                              {isWeekly ? "Weekly" : "Daily"} Performance Data
                            </span>
                          </Space>
                        }
                        extra={
                          <Space size="middle">
                            {/* Chart/Table Switch */}
                            <Space size={0}>
                              <div
                                onClick={() => onActiveTabChange("charts")}
                                style={{
                                  padding: "6px 12px",
                                  cursor: "pointer",
                                  backgroundColor:
                                    activeTab === "charts" ? "#1890ff" : "#fff",
                                  color:
                                    activeTab === "charts"
                                      ? "#ffffff"
                                      : "#595959",
                                  border: "1px solid #d9d9d9",
                                  borderRight: "none",
                                  borderTopLeftRadius: 6,
                                  borderBottomLeftRadius: 6,
                                  fontSize: 16,
                                  transition: "all 0.3s",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: 36,
                                  height: 32,
                                }}
                              >
                                <LineChartOutlined />
                              </div>
                              <div
                                onClick={() => onActiveTabChange("table")}
                                style={{
                                  padding: "6px 12px",
                                  cursor: "pointer",
                                  backgroundColor:
                                    activeTab === "table" ? "#1890ff" : "#fff",
                                  color:
                                    activeTab === "table"
                                      ? "#ffffff"
                                      : "#595959",
                                  border: "1px solid #d9d9d9",
                                  borderTopRightRadius: 6,
                                  borderBottomRightRadius: 6,
                                  fontSize: 16,
                                  transition: "all 0.3s",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: 36,
                                  height: 32,
                                }}
                              >
                                <TableOutlined />
                              </div>
                            </Space>
                            <Divider
                              orientation="vertical"
                              style={{ height: 20 }}
                            />
                            {/* Weekly/Daily Switch */}
                            <Space size="small">
                              <Text
                                type={isWeekly ? "primary" : "secondary"}
                                style={{
                                  fontSize: 14,
                                  fontWeight: isWeekly ? 600 : 400,
                                  transition: "all 0.3s",
                                }}
                              >
                                Weekly
                              </Text>
                              <Switch
                                checked={!isWeekly}
                                onChange={(checked) => onToggleWeekly(!checked)}
                                checkedChildren="D"
                                unCheckedChildren="W"
                                style={{
                                  background: !isWeekly ? "#1890ff" : "#d9d9d9",
                                }}
                              />
                              <Text
                                type={!isWeekly ? "primary" : "secondary"}
                                style={{
                                  fontSize: 14,
                                  fontWeight: !isWeekly ? 600 : 400,
                                  transition: "all 0.3s",
                                }}
                              >
                                Daily
                              </Text>
                            </Space>
                          </Space>
                        }
                        style={{
                          borderRadius: 12,
                          border: "1px solid #e8e8e8",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                        }}
                        variant="borderless"
                        styles={{
                          header: {
                            background:
                              "linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)",
                            borderRadius: "12px 12px 0 0",
                            padding: "16px 24px",
                          },
                          body: {
                            padding: "24px 24px 0 24px",
                          },
                        }}
                      >
                        {activeTab === "table" ? (
                          <Table
                            loading={loading}
                            columns={columns}
                            dataSource={
                              isWeekly ? tableData.weekly : tableData.daily
                            }
                            rowKey="key"
                            size="small"
                            expandable={{
                              expandedRowRender: (record) => (
                                <ContributorMetrics
                                  projectId={projectId}
                                  objectiveId={objectiveId}
                                  weekEndingDate={isWeekly ? record.date : null}
                                  range={
                                    isWeekly
                                      ? null
                                      : { start: record.date, end: record.date }
                                  }
                                  isWeekly={isWeekly}
                                  refreshTrigger={
                                    contributorMetricsRefreshTrigger
                                  }
                                  onRefresh={onRefreshContributorMetrics}
                                />
                              ),
                            }}
                            pagination={{
                              showSizeChanger: true,
                              pageSizeOptions: ["10", "20", "30", "50"],
                              showTotal: (total, range) =>
                                `${range[0]}-${range[1]} of ${total} ${isWeekly ? "weeks" : "days"}`,
                              showQuickJumper: false,
                              hideOnSinglePage: false,
                            }}
                            scroll={{ x: 800, y: "calc(100vh - 480px)" }}
                            locale={{
                              emptyText: (
                                <div
                                  style={{
                                    padding: "40px",
                                    textAlign: "center",
                                  }}
                                >
                                  <BarChartOutlined
                                    style={{
                                      fontSize: 48,
                                      color: "#d9d9d9",
                                      marginBottom: 16,
                                    }}
                                  />
                                  <div style={{ color: "#8c8c8c" }}>
                                    No performance data available
                                  </div>
                                </div>
                              ),
                            }}
                            style={{
                              borderRadius: 8,
                            }}
                            rowClassName={(index) =>
                              index % 2 === 0
                                ? "table-row-light"
                                : "table-row-dark"
                            }
                          />
                        ) : loading ? (
                          <div
                            style={{
                              marginTop: 48,
                              marginBottom: 48,
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <Spin description="Loading charts…" />
                          </div>
                        ) : (
                          <div style={{ marginBottom: "24px 0" }}>
                            <Row
                              gutter={[24, 24]}
                              style={{ marginBottom: "24px" }}
                            >
                              {/* Combined Chart */}
                              <Col xs={24} lg={24}>
                                <Card
                                  title={
                                    <Space>
                                      <AreaChartOutlined
                                        style={{ color: "#1890ff" }}
                                      />
                                      <span>Performance Overview</span>
                                    </Space>
                                  }
                                  style={{
                                    borderRadius: 8,
                                    border: "1px solid #e8e8e8",
                                  }}
                                  variant="borderless"
                                >
                                  <ResponsiveContainer
                                    width="100%"
                                    height={350}
                                  >
                                    <ComposedChart
                                      data={
                                        isWeekly
                                          ? chartData.weekly
                                          : chartData.daily
                                      }
                                      margin={{
                                        top: 10,
                                        right: 30,
                                        left: 0,
                                        bottom: 0,
                                      }}
                                    >
                                      <CartesianGrid
                                        strokeDasharray="3 3"
                                        stroke="#f0f0f0"
                                      />
                                      <XAxis
                                        dataKey="date"
                                        angle={-45}
                                        textAnchor="end"
                                        height={80}
                                        style={{ fontSize: 12 }}
                                      />
                                      <YAxis
                                        yAxisId="left"
                                        style={{ fontSize: 12 }}
                                        label={{
                                          value: "Total Hours",
                                          angle: -90,
                                          position: "insideLeft",
                                        }}
                                      />
                                      <YAxis
                                        yAxisId="right"
                                        orientation="right"
                                        style={{ fontSize: 12 }}
                                        label={{
                                          value: "Contributors",
                                          angle: 90,
                                          position: "insideRight",
                                        }}
                                      />
                                      <Tooltip
                                        formatter={(value, name) => {
                                          if (name === "totalCosts") {
                                            return formatCurrency(value);
                                          }
                                          if (name === "totalHours") {
                                            return `${formatNumber(value)} hrs`;
                                          }
                                          return value;
                                        }}
                                      />
                                      <Legend />
                                      <Bar
                                        barSize={20}
                                        zIndex={0}
                                        yAxisId="right"
                                        type="monotone"
                                        dataKey="contributors"
                                        fill="#52c41a"
                                        name="Contributors"
                                        strokeWidth={2}
                                        radius={[4, 4, 0, 0]}
                                        stroke="#52c41a"
                                      />
                                      <Area
                                        zIndex={1}
                                        yAxisId="left"
                                        type="monotone"
                                        dataKey="totalHours"
                                        fill="#fa8c16"
                                        fillOpacity={0.3}
                                        stroke="#fa8c16"
                                        strokeWidth={2}
                                        name="Total Hours"
                                      />
                                    </ComposedChart>
                                  </ResponsiveContainer>
                                </Card>
                              </Col>

                              {/* Total quantity (Hours/Units) Chart */}
                              <Col xs={24} lg={12}>
                                <Card
                                  title={
                                    <Space>
                                      <ClockCircleOutlined
                                        style={{ color: "#fa8c16" }}
                                      />
                                      <span>Total Hours</span>
                                    </Space>
                                  }
                                  style={{
                                    borderRadius: 8,
                                    border: "1px solid #e8e8e8",
                                  }}
                                  variant="borderless"
                                >
                                  <ResponsiveContainer
                                    width="100%"
                                    height={300}
                                  >
                                    <AreaChart
                                      data={
                                        isWeekly
                                          ? chartData.weekly
                                          : chartData.daily
                                      }
                                      margin={{
                                        top: 10,
                                        right: 10,
                                        left: 0,
                                        bottom: 0,
                                      }}
                                    >
                                      <defs>
                                        <linearGradient
                                          id="colorHours"
                                          x1="0"
                                          y1="0"
                                          x2="0"
                                          y2="1"
                                        >
                                          <stop
                                            offset="5%"
                                            stopColor="#fa8c16"
                                            stopOpacity={0.8}
                                          />
                                          <stop
                                            offset="95%"
                                            stopColor="#fa8c16"
                                            stopOpacity={0}
                                          />
                                        </linearGradient>
                                      </defs>
                                      <CartesianGrid
                                        strokeDasharray="3 3"
                                        stroke="#f0f0f0"
                                      />
                                      <XAxis
                                        dataKey="date"
                                        angle={-45}
                                        textAnchor="end"
                                        height={80}
                                        style={{ fontSize: 12 }}
                                      />
                                      <YAxis style={{ fontSize: 12 }} />
                                      <Tooltip
                                        formatter={(value) =>
                                          `${formatNumber(value)} hrs`
                                        }
                                      />
                                      <Area
                                        type="monotone"
                                        dataKey="totalHours"
                                        stroke="#fa8c16"
                                        strokeWidth={2}
                                        fillOpacity={1}
                                        fill="url(#colorHours)"
                                        name="Total Hours"
                                      />
                                    </AreaChart>
                                  </ResponsiveContainer>
                                </Card>
                              </Col>

                              {/* Contributors Chart */}
                              <Col xs={24} lg={12}>
                                <Card
                                  title={
                                    <Space>
                                      <TeamOutlined
                                        style={{ color: "#52c41a" }}
                                      />
                                      <span>Contributors</span>
                                    </Space>
                                  }
                                  style={{
                                    borderRadius: 8,
                                    border: "1px solid #e8e8e8",
                                  }}
                                  variant="borderless"
                                >
                                  <ResponsiveContainer
                                    width="100%"
                                    height={300}
                                  >
                                    <BarChart
                                      data={
                                        isWeekly
                                          ? chartData.weekly
                                          : chartData.daily
                                      }
                                      margin={{
                                        top: 10,
                                        right: 10,
                                        left: 0,
                                        bottom: 0,
                                      }}
                                    >
                                      <CartesianGrid
                                        strokeDasharray="3 3"
                                        stroke="#f0f0f0"
                                      />
                                      <XAxis
                                        dataKey="date"
                                        angle={-45}
                                        textAnchor="end"
                                        height={80}
                                        style={{ fontSize: 12 }}
                                      />
                                      <YAxis style={{ fontSize: 12 }} />
                                      <Tooltip />
                                      <Bar
                                        dataKey="contributors"
                                        fill="#52c41a"
                                        barSize={20}
                                        radius={[4, 4, 0, 0]}
                                        name="Contributors"
                                      />
                                    </BarChart>
                                  </ResponsiveContainer>
                                </Card>
                              </Col>
                            </Row>
                          </div>
                        )}
                      </Card>

                      {/* Contributor Summary Section */}
                      <Card
                        title={
                          <Space>
                            <ClockCircleOutlined
                              style={{ color: "#722ed1", fontSize: 18 }}
                            />
                            <span style={{ fontSize: 18, fontWeight: 600 }}>
                              Contributor Summary
                            </span>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              (All Time)
                            </Text>
                          </Space>
                        }
                        style={{
                          marginTop: 24,
                          borderRadius: 12,
                          border: "1px solid #e8e8e8",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                        }}
                        variant="borderless"
                        styles={{
                          header: {
                            background:
                              "linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%)",
                            borderRadius: "12px 12px 0 0",
                            padding: "16px 24px",
                          },
                          body: {
                            padding: "24px 24px 0 24px",
                          },
                        }}
                      >
                        <ContributorSummary
                          projectId={projectId}
                          objectiveId={objectiveId}
                        />
                      </Card>
                    </>
                  ),
                },
                {
                  key: "cohorts",
                  label: (
                    <Space>
                      <PartitionOutlined />
                      <span>Cohort Views</span>
                    </Space>
                  ),
                  children: (
                    <>
                      <CohortViews
                        projectId={projectId}
                        objectiveId={objectiveId}
                        timeframeDateRange={timeframeDateRange}
                      />
                    </>
                  ),
                },
              ]}
            />
          )}
        </Card>
      </div>
      <style>{`
        .table-row-light {
          background-color: #fafafa;
        }
        .table-row-dark {
          background-color: #ffffff;
        }
        .performance-table-row-selected {
          background-color: #bae7ff !important;
        }
        .ant-card-head-title {
          font-weight: 600;
        }
        .ant-statistic-title {
          margin-bottom: 8px;
        }
        .ant-descriptions-item-label {
          font-weight: 500;
        }
      `}</style>
    </div>
  );
};

export default PerformanceView;
